import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { 
  Building2, Loader2, CheckCircle2, 
  Phone, AlertTriangle, Briefcase, MapPin, Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "/logos/sterilemate-white.png?v=4";

const DEPARTMENTS = [
  { id: "SC", name: "Supply Chain & Logistics", roles: ["Backorders", "Procurement", "Quotes"] },
  { id: "FE", name: "Facilities Engineering", roles: ["Plumbing", "Electrical", "HVAC", "Medical Gas"] },
  { id: "BIOMED", name: "Biomedical Engineering", roles: ["Equipment Repair", "Calibration", "PM Scheduling", "Device Tracking"] },
  { id: "ENV", name: "Environmental Services", roles: ["Air Quality", "Water Sampling", "Infection Control"] },
  { id: "SPD", name: "Sterile Processing", roles: ["AAMI ST108", "Water Purification", "Equipment"] },
  { id: "NURSING", name: "Nursing & Clinical", roles: ["Patient Care", "Unit Management", "Clinical Support"] },
  { id: "IT", name: "Information Technology", roles: ["Systems Admin", "Network", "Help Desk", "Security"] },
  { id: "PM", name: "Project Management", roles: ["Renovations", "New Installs", "Annual Service"] },
  { id: "SAFETY", name: "Safety & Compliance", roles: ["Regulatory", "Risk Management", "Accreditation"] },
  { id: "ADMIN", name: "Administration", roles: ["Director", "Manager", "Executive"] },
  { id: "OTHER", name: "Other", roles: ["General Staff", "Contract Worker"] }
];

const FACILITY_TYPES = [
  { value: "hospital", label: "Hospital" },
  { value: "clinic", label: "Clinic" },
  { value: "office", label: "Office" },
  { value: "warehouse", label: "Warehouse" },
  { value: "other", label: "Other" }
];

export default function HospitalSetupPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [healthSystem, setHealthSystem] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [showAddFacility, setShowAddFacility] = useState(false);
  const [addingFacility, setAddingFacility] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    department: "",
    department_id: "",
    phone: "",
    facility_id: "",
    facility_name: "",
    password: "",
    confirm_password: ""
  });

  const [newFacility, setNewFacility] = useState({
    name: "",
    facility_type: "hospital",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: ""
  });

  // Verify token on load
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await fetch(`${API}/hospital/setup/${token}`);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.detail || "Invalid or expired setup link");
        }
        setUserData(data.user);
        setHealthSystem(data.health_system);
        
        // Fetch facilities for this health system
        if (data.health_system?.id) {
          fetchFacilities(data.health_system.id);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const fetchFacilities = async (healthSystemId) => {
    try {
      const res = await fetch(`${API}/health-systems/${healthSystemId}/facilities`);
      if (res.ok) {
        const data = await res.json();
        setFacilities(data.facilities || []);
      }
    } catch (err) {
      console.error("Failed to fetch facilities", err);
    }
  };

  const handleAddFacility = async () => {
    if (!newFacility.name.trim()) {
      toast.error("Facility name is required");
      return;
    }

    setAddingFacility(true);
    try {
      const res = await fetch(`${API}/health-systems/${healthSystem.id}/facilities/suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newFacility)
      });

      if (res.ok) {
        const data = await res.json();
        if (data.status === "exists") {
          toast.info("This facility already exists");
          setFormData({ ...formData, facility_id: data.facility.id, facility_name: data.facility.name });
        } else {
          toast.success("Facility added successfully");
          setFacilities([...facilities, data.facility]);
          setFormData({ ...formData, facility_id: data.facility.id, facility_name: data.facility.name });
        }
        setShowAddFacility(false);
        setNewFacility({
          name: "",
          facility_type: "hospital",
          address: "",
          city: "",
          state: "",
          zip_code: "",
          phone: ""
        });
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add facility");
      }
    } catch (err) {
      toast.error("Failed to add facility");
    } finally {
      setAddingFacility(false);
    }
  };

  const handleFacilitySelect = (value) => {
    if (value === "add_new") {
      setShowAddFacility(true);
    } else {
      const facility = facilities.find(f => f.id === value);
      setFormData({ 
        ...formData, 
        facility_id: value, 
        facility_name: facility?.name || "" 
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.department_id) {
      toast.error("Please select your department");
      return;
    }

    if (!formData.facility_id && facilities.length > 0) {
      toast.error("Please select your facility/location");
      return;
    }
    
    // Validate password
    if (!formData.password || formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    // Check password strength
    const hasUppercase = /[A-Z]/.test(formData.password);
    const hasLowercase = /[a-z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
    
    if (!(hasUppercase && hasLowercase && hasNumber)) {
      toast.error("Password must contain uppercase, lowercase, and a number");
      return;
    }
    
    if (formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/hospital/setup/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password: formData.password,
          department_id: formData.department_id,
          department: formData.department,
          title: formData.title || null
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Setup failed");
      }
      
      const data = await res.json();
      toast.success("Account activated! Welcome to AskIsaac.");
      
      // Store the hospital-specific token
      if (data.token) {
        localStorage.setItem("hospital_token", data.token);
        if (data.user) {
          localStorage.setItem("hospital_user", JSON.stringify(data.user));
        }
        if (data.health_system) {
          localStorage.setItem("hospital_health_system", JSON.stringify(data.health_system));
        }
        
        // Redirect to hospital portal
        navigate("/hospital/portal");
      } else {
        navigate("/hospital/login");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Setup Link Invalid</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate("/hospital/login")} variant="outline" className="border-slate-600 text-slate-300">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Sterile Mate" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Welcome to AskIsaac</h1>
          <p className="text-slate-400">Hospital Portal Setup</p>
        </div>

        {/* User Info Card */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-white font-semibold">{userData?.name}</p>
              <p className="text-slate-400 text-sm">{healthSystem?.name}</p>
            </div>
          </div>
          <p className="text-blue-400 text-sm">
            <CheckCircle2 className="w-4 h-4 inline mr-1" />
            Your account is ready. Complete your profile to get started.
          </p>
        </div>

        {/* Setup Form */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Facility Selection - QuickBooks style */}
            <div>
              <Label className="text-slate-300">Facility/Location *</Label>
              <Select 
                value={formData.facility_id} 
                onValueChange={handleFacilitySelect}
              >
                <SelectTrigger className="mt-1 bg-slate-900 border-slate-600 text-white" data-testid="facility-select">
                  <SelectValue placeholder="Select your facility" />
                </SelectTrigger>
                <SelectContent>
                  {facilities.map(facility => (
                    <SelectItem key={facility.id} value={facility.id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <div>
                          <span>{facility.name}</span>
                          {facility.is_main_facility && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Main</span>
                          )}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="add_new" className="text-blue-400">
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      <span>Add New Location...</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {formData.facility_name && (
                <p className="text-xs text-slate-500 mt-1">Selected: {formData.facility_name}</p>
              )}
            </div>

            <div>
              <Label className="text-slate-300">Job Title</Label>
              <div className="relative mt-1">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g. Facilities Manager"
                  className="pl-10 bg-slate-900 border-slate-600 text-white"
                  data-testid="title-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Department *</Label>
              <Select 
                value={formData.department_id} 
                onValueChange={(value) => {
                  const dept = DEPARTMENTS.find(d => d.id === value);
                  setFormData({...formData, department_id: value, department: dept?.name || ""});
                }}
              >
                <SelectTrigger className="mt-1 bg-slate-900 border-slate-600 text-white" data-testid="department-select">
                  <SelectValue placeholder="Select your department" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map(dept => (
                    <SelectItem key={dept.id} value={dept.id}>
                      <div>
                        <span className="font-medium">{dept.name}</span>
                        <span className="text-slate-400 text-xs ml-2">({dept.roles.slice(0, 2).join(", ")})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Phone Number</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(800) 988-3892"
                  className="pl-10 bg-slate-900 border-slate-600 text-white"
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Password Creation */}
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700 space-y-4">
              <div>
                <Label className="text-slate-300">Create Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Min 8 characters"
                  className="mt-1 bg-slate-900 border-slate-600 text-white"
                  data-testid="password-input"
                />
                <p className="text-xs text-slate-500 mt-1">Must include uppercase, lowercase, and number</p>
              </div>
              <div>
                <Label className="text-slate-300">Confirm Password *</Label>
                <Input
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                  placeholder="Re-enter your password"
                  className="mt-1 bg-slate-900 border-slate-600 text-white"
                  data-testid="confirm-password-input"
                />
              </div>
              <p className="text-xs text-slate-400">
                🔒 This will be your login password. Use it with your email to sign in.
              </p>
            </div>

            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6"
              data-testid="complete-setup-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Complete Setup</>
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Already set up?{" "}
          <a href="/hospital/login" className="text-blue-400 hover:underline">
            Sign in
          </a>
        </p>
      </div>

      {/* Add Facility Modal */}
      <Dialog open={showAddFacility} onOpenChange={setShowAddFacility}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Plus className="w-5 h-5 text-blue-400" />
              Add New Location
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new facility/location for {healthSystem?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Facility Name *</Label>
              <Input
                value={newFacility.name}
                onChange={(e) => setNewFacility({...newFacility, name: e.target.value})}
                placeholder="e.g. Ben Taub Hospital"
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="new-facility-name"
              />
            </div>

            <div>
              <Label className="text-slate-300">Facility Type</Label>
              <Select 
                value={newFacility.facility_type} 
                onValueChange={(value) => setNewFacility({...newFacility, facility_type: value})}
              >
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FACILITY_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Address</Label>
              <Input
                value={newFacility.address}
                onChange={(e) => setNewFacility({...newFacility, address: e.target.value})}
                placeholder="Street address"
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-slate-300 text-xs">City</Label>
                <Input
                  value={newFacility.city}
                  onChange={(e) => setNewFacility({...newFacility, city: e.target.value})}
                  placeholder="City"
                  className="bg-slate-900 border-slate-600 text-white text-sm"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">State</Label>
                <Input
                  value={newFacility.state}
                  onChange={(e) => setNewFacility({...newFacility, state: e.target.value})}
                  placeholder="TX"
                  className="bg-slate-900 border-slate-600 text-white text-sm"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">ZIP</Label>
                <Input
                  value={newFacility.zip_code}
                  onChange={(e) => setNewFacility({...newFacility, zip_code: e.target.value})}
                  placeholder="77030"
                  className="bg-slate-900 border-slate-600 text-white text-sm"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input
                value={newFacility.phone}
                onChange={(e) => setNewFacility({...newFacility, phone: e.target.value})}
                placeholder="(713) 873-2000"
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFacility(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleAddFacility} disabled={addingFacility} className="bg-blue-600 hover:bg-blue-700">
              {addingFacility ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Adding...</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> Add Location</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
